import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '../ui/badge';
import { Bell, ChevronDown, LogOut, User, Settings, RefreshCw } from 'lucide-react';
import ProfileModal from '../profile/ProfileModal';

const Header = ({ unreadCount = 0, onNotificationClick }) => {
  const { user, logout, selectedRole, switchRole } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const primaryRole = selectedRole || user?.roles?.[0] || 'user';
  const hasMultipleRoles = user?.roles?.length > 1;
  const otherRoles = (user?.roles || []).filter(r => r !== primaryRole);

  const ROLE_LABELS = {
    admin: 'Admin',
    underwriter: 'Underwriter',
    jr_underwriter: 'Jr Underwriter',
    broker: 'Broker',
    borrower: 'Borrower',
  };
  const formatRole = (r) => ROLE_LABELS[r] || (r ? r.charAt(0).toUpperCase() + r.slice(1) : '');

  const getRoleColor = (role) => {
    const map = {
      admin: 'bg-amber-400',
      underwriter: 'bg-[#5EDEE5]',
      jr_underwriter: 'bg-fuchsia-400',
      broker: 'bg-sky-400',
      borrower: 'bg-violet-400',
    };
    return map[role] || 'bg-slate-400';
  };

  return (
    <header className="sticky top-0 z-50 h-16 flex items-center justify-between px-8 bg-[#081120]">
      {/* Left: Logo + Title */}
      <div className="flex items-center gap-5">
        <div className="overflow-hidden" style={{ height: '40px', width: '160px' }}>
          <img
            src="https://customer-assets.emergentagent.com/job_e22a09b6-65e1-4ed8-871e-1cefd41bd269/artifacts/5h3c4vac_C44041E2-FC46-43BE-B355-CB56285A03E8.png"
            alt="Quidity Direct"
            className="w-full h-full"
            style={{ objectFit: 'cover' }}
          />
        </div>
        <div className="h-6 w-px bg-white/15" />
        <div className="flex items-center gap-2.5">
          <h1 className="text-sm font-semibold text-white/90 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {formatRole(primaryRole)} Dashboard
          </h1>
          <Badge className="bg-[#162538] text-[#5EDEE5] border border-[#5EDEE5]/30 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-widest uppercase" data-testid="role-badge">
            {formatRole(primaryRole)}
          </Badge>
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-2">
        {/* Role Switcher for multi-role users */}
        {hasMultipleRoles && otherRoles.length > 0 && (
          <div className="flex items-center gap-1.5 mr-2">
            <span className="text-[10px] text-white/40 uppercase tracking-wider font-semibold mr-1">Switch:</span>
            {otherRoles.map(role => (
              <button
                key={role}
                onClick={() => switchRole(role)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 border border-white/10 text-white text-xs font-medium transition-all"
                data-testid={`switch-role-${role}`}
              >
                <span className={`w-2 h-2 rounded-full ${getRoleColor(role)}`} />
                {formatRole(role)}
              </button>
            ))}
          </div>
        )}

        <button
          onClick={onNotificationClick}
          className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
          data-testid="notification-bell"
        >
          <Bell className="h-[18px] w-[18px] text-white/70" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="user-menu-button"
          >
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#66C2CA] to-[#5EDEE5] flex items-center justify-center text-[#081120] text-xs font-bold">
              {user?.full_name?.charAt(0) || 'U'}
            </div>
            <span className="text-sm font-medium text-white hidden sm:block">{user?.full_name || 'User'}</span>
            <ChevronDown className={`h-3.5 w-3.5 text-white/50 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showDropdown && (
            <div className="absolute right-0 mt-2 w-56 rounded-xl bg-white border border-slate-200 py-1.5 z-50"
              style={{ boxShadow: '0 12px 48px rgba(8,17,32,0.18)' }}>
              <div className="px-4 py-3 border-b border-slate-100">
                <p className="text-sm font-medium text-[#081120]">{user?.full_name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{user?.email}</p>
              </div>
              {hasMultipleRoles && (
                <div className="px-4 py-2.5 border-b border-slate-100">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Switch Role</p>
                  <div className="flex flex-wrap gap-1.5">
                    {user.roles.map(role => (
                      <button
                        key={role}
                        onClick={() => { switchRole(role); setShowDropdown(false); }}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${role === primaryRole ? 'bg-[#081120] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                        data-testid={`dropdown-switch-${role}`}
                      >
                        {formatRole(role)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <button
                onClick={() => { setShowProfile(true); setShowDropdown(false); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                data-testid="profile-button"
              >
                <User className="h-4 w-4" /> Profile
              </button>
              <div className="border-t border-slate-100 mt-1 pt-1">
                <button onClick={logout} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors" data-testid="logout-button">
                  <LogOut className="h-4 w-4" /> Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Profile modal — file uploads + change password */}
      <ProfileModal open={showProfile} onClose={() => setShowProfile(false)} />
    </header>
  );
};

export default Header;
